/**
 * MLinkOne PO Workbench thunks — Phase A (browser). Read filters/paging/sort from the
 * slice and call poWorkbenchService. Tenant id is passed by the caller (read from the
 * active-tenant Redux state at the component boundary).
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import {
  poWorkbenchService,
  type PoListResponse,
  type PoCanonicalResponse,
  type PoIngestBatchResponse,
  type PoRejectedListResponse,
  type PoReprocessResponse,
} from '@/app/services/api/poWorkbenchService';

/** Sentinel reject value for an aborted request (StrictMode re-mount / superseded fetch). */
export const PO_FETCH_ABORTED = '__po_fetch_aborted__';

/** axios CanceledError / DOM AbortError — a cancellation, not a real failure. */
function isAbort(e: unknown): boolean {
  const x = e as { code?: string; name?: string } | null;
  return !!x && (x.code === 'ERR_CANCELED' || x.name === 'CanceledError' || x.name === 'AbortError');
}

function errMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  return fallback;
}

/** Load one page of the PO list for the given tenant, using the slice's current filters/paging/sort. */
export const fetchPoList = createAsyncThunk<
  PoListResponse,
  { tenantId: number; buyerPartyId?: number | null; supplierPartyId?: number | null; signal?: AbortSignal },
  { state: RootState; rejectValue: string }
>('poWorkbench/fetchPoList', async ({ tenantId, buyerPartyId, supplierPartyId, signal }, { getState, rejectWithValue }) => {
  const b = getState().poWorkbench.browser;
  try {
    return await poWorkbenchService.listPo(
      {
        tenantId,
        // Active party (top-bar) scopes the list.
        buyerPartyId: buyerPartyId ?? undefined,
        supplierPartyId: supplierPartyId ?? undefined,
        poNumber: b.filters.poNumber || undefined,
        dateFrom: b.filters.dateFrom || undefined,
        dateTo: b.filters.dateTo || undefined,
        currentOnly: true,
        pageIndex: b.pageIndex,
        pageSize: b.pageSize,
        sortBy: b.sortBy || undefined,
        sortDir: b.sortDir || undefined,
      },
      signal,
    );
  } catch (e) {
    if (isAbort(e)) return rejectWithValue(PO_FETCH_ABORTED);
    return rejectWithValue(errMessage(e, 'Failed to load PO list.'));
  }
});

/** Synchronously import N Doc850 files; returns the per-file result + summary. */
export const ingestPoBatch = createAsyncThunk<
  PoIngestBatchResponse,
  { tenantId: number; files: File[]; signal?: AbortSignal },
  { rejectValue: string }
>('poWorkbench/ingestPoBatch', async ({ tenantId, files, signal }, { rejectWithValue }) => {
  try {
    return await poWorkbenchService.ingestPoBatch(tenantId, files, signal);
  } catch (e) {
    if (isAbort(e)) return rejectWithValue(PO_FETCH_ABORTED);
    return rejectWithValue(errMessage(e, 'PO import failed.'));
  }
});

/** Load one page of rejected (not-enrolled) PO landings, scoped to the active party. */
export const fetchRejectedPo = createAsyncThunk<
  PoRejectedListResponse,
  { tenantId: number; buyerPartyId?: number | null; supplierPartyId?: number | null; pageIndex?: number; pageSize?: number; signal?: AbortSignal },
  { rejectValue: string }
>('poWorkbench/fetchRejectedPo', async ({ tenantId, buyerPartyId, supplierPartyId, pageIndex, pageSize, signal }, { rejectWithValue }) => {
  try {
    return await poWorkbenchService.listRejectedPo({ tenantId, buyerPartyId, supplierPartyId, pageIndex, pageSize }, signal);
  } catch (e) {
    if (isAbort(e)) return rejectWithValue(PO_FETCH_ABORTED);
    return rejectWithValue(errMessage(e, 'Failed to load rejected POs.'));
  }
});

/** Reprocess one rejected PO landing after its pair is enrolled. */
export const reprocessPo = createAsyncThunk<
  PoReprocessResponse,
  { tenantId: number; sourceDocId: number },
  { rejectValue: string }
>('poWorkbench/reprocessPo', async ({ tenantId, sourceDocId }, { rejectWithValue }) => {
  try {
    return await poWorkbenchService.reprocessPo(tenantId, sourceDocId);
  } catch (e) {
    return rejectWithValue(errMessage(e, 'Reprocess failed.'));
  }
});

/** Load (or refresh) the canonical drill-down for one PO. */
export const fetchPoCanonical = createAsyncThunk<
  PoCanonicalResponse,
  { tenantId: number; poId: number; signal?: AbortSignal },
  { rejectValue: string }
>('poWorkbench/fetchPoCanonical', async ({ tenantId, poId, signal }, { rejectWithValue }) => {
  try {
    return await poWorkbenchService.getPoCanonical(tenantId, poId, signal);
  } catch (e) {
    if (isAbort(e)) return rejectWithValue(PO_FETCH_ABORTED);
    return rejectWithValue(errMessage(e, 'Failed to load PO.'));
  }
});
